<script setup lang="ts">
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';
    import ValidateVatNumberEndpoint, { ValidatedVatNumberResponse } from '~~/app/common/endpoints/ValidateVatNumberEndpoint';
    const emit = defineEmits(['validVatNumber']);

    const input = ref<HTMLInputElement | null>(null);
    const props = defineProps<FormInputProps>();
    const inputData: FormInputData = useFormInput(props);
    const endpoint = new ValidateVatNumberEndpoint();
    const response = ref<ValidatedVatNumberResponse | null>(null);
    const validatingVatNumber = ref(false);
    const isDirty = ref(false);
    const { abortable, abort } = useAbortableFetch();

    const validateVatNumber = async () => {
        setIsDirty(false);
        abort();
        validatingVatNumber.value = true;
        if (inputData.form.errors?.has(inputData.inputName)) {
            inputData.form.errors.clear(inputData.inputName);
        }

        if (!inputData.model.value) {
            validatingVatNumber.value = false;
            response.value = null;
            return;
        }

        const formattedVatNumber: string = inputData.model.value.replace(/[./\s]/g, '').toUpperCase();
        inputData.model.value = formattedVatNumber;

        try {
            response.value = await endpoint.withAbortController(abortable.value).validate(formattedVatNumber);

            if (response.value && response.value.data) {
                emit('validVatNumber', response.value);
            }

            validatingVatNumber.value = false;
        } catch (error) {}
    };

    const isValid = computed(() => {
        return !inputData.model.value ? false : response.value?.data;
    });

    const setIsDirty = (value: boolean) => {
        isDirty.value = value;
    };

    defineExpose({
        isValid,
    });

    onMounted(async () => {
        if (props.autofocus && input.value) {
            await wait(250);
            input.value?.focus();
        }
    });

    onBeforeUnmount(() => abort());
</script>

<template>
    <FormElement :input-data="inputData">
        <input
            :id="inputData.inputId.value"
            ref="input"
            v-model="inputData.model.value"
            :class="inputData.className.value"
            :name="inputData.props.inputName"
            :placeholder="inputData.placeholder.value"
            :type="inputData.props.inputType"
            :disabled="inputData.props.disabled"
            @change="validateVatNumber"
            @input="() => setIsDirty(true)"
        />
        <template v-if="inputData.model.value">
            <template v-if="validatingVatNumber">
                <span class="text-[11px] text-gray-400">
                    <i class="fa-duotone fa-loader fa-spin"></i>
                    {{ $t('misc.vat_number_input.validating') }}
                </span>
            </template>
            <template v-if="!validatingVatNumber && response && !isDirty">
                <span
                    v-if="response.error && !inputData.form.errors?.has(inputData.inputName)"
                    class="text-[11px] text-red-600"
                >
                    <i class="fa-regular fa-xmark" />
                    {{ response.error.message }}
                </span>
                <span
                    v-if="response.data"
                    class="text-[11px] text-green-600"
                >
                    <i class="fa-regular fa-check" />
                    {{ $t('misc.vat_number_input.valid') }}
                </span>
            </template>
        </template>
    </FormElement>
</template>
